import { List } from '@mui/material';
import { ICustomPageSection } from '../../../models';
import { Clear, Edit, RestartAlt, Save } from '@mui/icons-material';
import { Box, Divider, IconButton, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import { Link } from '../../../components/link';

interface IAccordionSubContainer {
  subLinks: ICustomPageSection[];
  item: ICustomPageSection;
  isAccordionExpanded: {
    [item: string]: boolean;
  };
  setAccordionExpanded: (val: { [item: string]: boolean }) => void;
  isEditing: {
    [id: string]: boolean;
  };
  setEditing: (val: { [id: number]: boolean }) => void;
  setSections: (val: ICustomPageSection[]) => void;
  sections: ICustomPageSection[];
  grandParentIndex: number;
  parentIndex: number;
  index: number;
}

export const AccordionSubContainer = ({
  isAccordionExpanded,
  setAccordionExpanded,
  isEditing,
  setEditing,
  item,
  setSections,
  sections,
  grandParentIndex,
  parentIndex,
  index
}: IAccordionSubContainer) => {
  const copy = JSON.parse(JSON.stringify(sections));
  const currentItem = copy?.[grandParentIndex]?.sections?.[parentIndex]?.sections?.[index];
  const isInEditMode = isEditing[item.customPageSectionId];
  return (
    <div>
      <List component="div" disablePadding>
        <div>
          {isInEditMode && <Divider sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />}
          <ListItem sx={{ padding: '6px 0 0 18px' }}>
            {isInEditMode ? (
              <>
                <Box
                  sx={{
                    margin: '0.5rem 0',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    marginRight: '1rem'
                  }}
                >
                  <TextField
                    size="small"
                    label="Link Title"
                    autoComplete="nope"
                    variant="outlined"
                    value={currentItem.name}
                    required
                    sx={{ marginBottom: '0.75rem' }}
                    onChange={e => {
                      currentItem.name = e.target.value;
                      setSections(copy);
                    }}
                    error={isInEditMode && !currentItem.name ? true : false}
                    helperText={isInEditMode && !currentItem.name && 'Required'}
                  />
                  <TextField
                    size="small"
                    label="Link text"
                    autoComplete="nope"
                    variant="outlined"
                    value={currentItem.linkText}
                    sx={{ marginBottom: '0.75rem' }}
                    onChange={e => {
                      currentItem.linkText = e.target.value;
                      setSections(copy);
                    }}
                  />
                  <TextField
                    size="small"
                    label="Link URL"
                    autoComplete="nope"
                    variant="outlined"
                    value={currentItem.linkUrl}
                    required
                    sx={{ marginBottom: '0.75rem' }}
                    onChange={e => {
                      currentItem.linkUrl = e.target.value;
                      setSections(copy);
                    }}
                    error={
                      // required field for a sub route link
                      (isInEditMode && !currentItem.linkUrl) ||
                      // not a valid relative route
                      !currentItem.linkUrl?.startsWith('http') ||
                      // can't contain white space
                      currentItem?.linkUrl?.indexOf(' ') >= 0
                        ? true
                        : false
                    }
                    helperText={
                      isInEditMode && !currentItem.linkUrl
                        ? 'Required'
                        : !currentItem.linkUrl.startsWith('http')
                        ? 'Must start with http'
                        : currentItem?.linkUrl?.indexOf(' ') >= 0
                        ? 'Must not contain white space'
                        : ''
                    }
                  />
                </Box>
              </>
            ) : (
              <ListItemText>
                <Typography>{item.name}</Typography>
                <Link to={`/${item.linkUrl}`}>{item.linkUrl}</Link>
              </ListItemText>
            )}
            <Box display="flex" flexDirection={isInEditMode ? 'column' : 'row'} alignSelf={isInEditMode ? 'flex-start' : 'auto'}>
              <IconButton
                onClick={e => {
                  setEditing({
                    [item.customPageSectionId]: !isInEditMode
                  });
                }}
                sx={{ p: '2px' }}
                title={isInEditMode ? 'Save' : 'Edit'}
                disabled={isInEditMode && (!currentItem.name || !currentItem.linkUrl)}
                color={isInEditMode ? 'primary' : 'inherit'}
              >
                {isInEditMode ? <Save /> : <Edit />}
              </IconButton>
              {isInEditMode && (
                <IconButton
                  title="Reset"
                  color="secondary"
                  sx={{ p: '2px' }}
                  onClick={e => {
                    setSections(sections);
                  }}
                >
                  <RestartAlt />
                </IconButton>
              )}
              <IconButton
                color="error"
                title="Remove"
                sx={{ p: '2px' }}
                onClick={() => {
                  const result = window.confirm('Are you sure you want to remove this item?');
                  if (result) {
                    const newItems = copy[grandParentIndex].sections[parentIndex].sections.filter(l => l.customPageSectionId !== item.customPageSectionId);
                    copy[grandParentIndex].sections[parentIndex].sections = newItems;
                    // close the menu if they remove the last sub item
                    if (copy[grandParentIndex].sections[parentIndex].sections.length === 0) {
                      setAccordionExpanded({
                        ...isAccordionExpanded,
                        [item.name]: !isAccordionExpanded[item.name]
                      });
                    }

                    setSections(copy);
                  }
                }}
              >
                <Clear />
              </IconButton>
            </Box>
          </ListItem>
          {isInEditMode && <Divider sx={{ marginBottom: '0.5rem' }} />}
        </div>
      </List>
    </div>
  );
};
